import axios from 'axios';
import { DHLError, axiosRequest, handleHttpResponse } from '@general/js/utils';
import AbstractClass from './TaxStatementsAbstractServices';
import { Services } from '../Services';

export default class TaxStatementsServices extends AbstractClass {
  /**
   * Returns a promise for fetching data of tax statements
   *
   * @param {string} memberNumber - Member Number is used for get tax statements.
   * @param {number} numberOfYears - Number of years before current date to fetch tax statements from
   * @param {number} currentYear - Year to start lookback from, in the format of 'YYYY'
   * @param {Object} [options] - Optional parameters
   * @param {string} [options.generatedSinceTimestamp] - ISO 8601 UTC timestamp; when provided, EAI polls for a document newer than this timestamp
   *
   */
  static async getTaxStatementSummary(memberNumber, numberOfYears, currentYear = new Date().getFullYear(), options = {}) {
    try {
      const params = {
        numberOfYears,
        currentYear,
      };

      if (options.generatedSinceTimestamp) {
        params.generatedSinceTimestamp = options.generatedSinceTimestamp.substring(0, 19);
      }

      const queryParams = { params };

      const response = await axiosRequest().get(Services.TAX_STATEMENTS.GET_TAX_STATEMENTS_SUMMARY(memberNumber), queryParams);

      if (handleHttpResponse(response) && response?.data?.members) {
        return response.data.members;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getTaxStatementSummary() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Returns a promise for fetching a particular tax statement document for a given person
   *
   * @param {string} memberNumber - Member number (policy number) of the policy
   * @param {string} accessingPersonCode - Person code of who's tax statement document is being accessed
   * @param {string} documentId - Document Id of the tax document to be fetched
   *
   */
  static async getTaxStatementDocument(memberNumber, accessingPersonCode, documentId) {
    try {
      const response = await axiosRequest().get(Services.TAX_STATEMENTS.GET_TAX_STATEMENT_DOCUMENT(memberNumber, accessingPersonCode, documentId));

      if (handleHttpResponse(response) && response?.data?.file?.data) {
        return response.data.file.data;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getTaxStatementDocument() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for content information on tax statements
   */
  static async getReferenceData() {
    try {
      const response = await axios.get(Services.TAX_STATEMENTS.GET_REFERENCE_DATA);
      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`getReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Requests on-demand generation of a tax statement for the given financial year.
   *
   * @param {string} memberNumber - Member number (policy number) of the policy
   * @param {string} financialYear - Financial year ending year as a string, e.g. "2025" for FY 2024/25
   *
   */
  static async generateTaxStatement(memberNumber, financialYear) {
    try {
      const response = await axiosRequest().post(Services.TAX_STATEMENTS.GENERATE_TAX_STATEMENT(memberNumber), { financialYear });
      return response.data.generatedAt;
    } catch (error) {
      throw new DHLError(`generateTaxStatement() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }
}
